import { Address, CustomerWalletStatusType } from 'types/customers'
import { Currencies } from 'types/types'
import {
  CryptoSendAndReceiveOptions,
  CustomerModels,
  FiatPayinAndPayoutOptions,
  GetCryptoSendAndReceiveOptionsProps,
  GetCustomerAddressOptions,
  GetFiatPayinAndPayoutOptionsProps,
  UseCustomerWalletActionsProps,
  WalletActionOptions,
} from './types'

export const getCustomerAddress = (address: Address, options?: GetCustomerAddressOptions) => {
  const addressLines = [address?.addressLine1, address?.addressLine2, address?.city]
  if (options?.useFullAddress) {
    addressLines?.push(address?.postalCode, address?.stateCode, address?.countryCode)
  } else if (options?.useAddressExcludingCountry) {
    addressLines?.push(address?.postalCode, address?.stateCode)
  } else if (options?.usePostalCode) {
    addressLines?.push(address?.postalCode)
  } else {
    addressLines?.push(address?.country)
  }
  return addressLines?.filter(Boolean)?.join(', ')
}

const getCryptoSendAndReceiveOptions = ({
  customerWallet,
  isEmbeddedOrRelianceCustomerModel,
  isViewOnlyRole,
  isActive,
  isEpcWalletPayoutsCapabilityEnabled,
  isEpcWalletPayinsCapabilityEnabled,
  isEpcWalletInternalTransfersCapabilityEnabled,
  supportsInternalTransfers,
}: GetCryptoSendAndReceiveOptionsProps): CryptoSendAndReceiveOptions => {
  const isCryptoSendEnabled =
    customerWallet?.balance?.value > 0 && isEmbeddedOrRelianceCustomerModel
  const isCryptoReceiveEnabled = isEmbeddedOrRelianceCustomerModel
  const isActiveAndNotViewOnlyRole = !isViewOnlyRole && isActive

  // Crypto sends
  const isCryptoSendsEnabled = !isViewOnlyRole && isActive && isCryptoSendEnabled
  const isCryptoSendOptionEnabled = isEmbeddedOrRelianceCustomerModel
    ? isCryptoSendsEnabled && isEpcWalletPayoutsCapabilityEnabled
    : isCryptoSendsEnabled

  // Crypto receives
  const isCryptoReceiveOptionEnabled = isEmbeddedOrRelianceCustomerModel
    ? isActiveAndNotViewOnlyRole && isCryptoReceiveEnabled && isEpcWalletPayinsCapabilityEnabled
    : isActiveAndNotViewOnlyRole && isCryptoReceiveEnabled

  // Internal transfers
  const isCryptoWallet = isCryptoCustomerWallet(customerWallet?.balance.currencyCode)
  const isMoveEnabled = supportsInternalTransfers && isActiveAndNotViewOnlyRole
  const isCryptoMoveOptionEnabled =
    isEmbeddedOrRelianceCustomerModel && isCryptoWallet
      ? isMoveEnabled && isEpcWalletInternalTransfersCapabilityEnabled
      : isMoveEnabled

  return {
    isCryptoSendOptionEnabled,
    isCryptoReceiveOptionEnabled,
    isCryptoMoveOptionEnabled,
  }
}

const getFiatPayinAndPayoutOptions = ({
  isEmbeddedOrRelianceCustomerModel,
  isViewOnlyRole,
  supportsThirdParty,
  isActive,
  isEpcWalletPayoutsCapabilityEnabled,
  isEpcWalletPayinsCapabilityEnabled,
  supportsDeposits,
  isEpcWalletInternalTransfersCapabilityEnabled,
}: GetFiatPayinAndPayoutOptionsProps): FiatPayinAndPayoutOptions => {
  const isActiveAndNotViewOnlyRole = !isViewOnlyRole && isActive

  // Unified payout
  const isPayoutEnabled = isActiveAndNotViewOnlyRole && supportsThirdParty
  const isUnifiedPayoutEnabled = isEmbeddedOrRelianceCustomerModel
    ? isPayoutEnabled && isEpcWalletPayoutsCapabilityEnabled
    : isPayoutEnabled
  const isUnifiedPayoutOptionEnabled = isEmbeddedOrRelianceCustomerModel
    ? isUnifiedPayoutEnabled && isEpcWalletPayoutsCapabilityEnabled
    : isUnifiedPayoutEnabled

  // Internal transfers
  const isFiatMoveOptionEnabled = isEmbeddedOrRelianceCustomerModel
    ? isActiveAndNotViewOnlyRole && isEpcWalletInternalTransfersCapabilityEnabled
    : isActiveAndNotViewOnlyRole

  // Fiat deposits
  const isFiatDepositEnabled = isActiveAndNotViewOnlyRole && supportsDeposits
  const isFiatDepositOptionEnabled = isEmbeddedOrRelianceCustomerModel
    ? isFiatDepositEnabled && isEpcWalletPayinsCapabilityEnabled
    : isFiatDepositEnabled

  return {
    isUnifiedPayoutOptionEnabled,
    isFiatMoveOptionEnabled,
    isFiatDepositOptionEnabled,
  }
}

export const isCryptoCustomerWallet = (currency: string) => {
  if (!currency) return true

  const fiatCurrencies: string[] = [
    Currencies.AED,
    Currencies.EUR,
    Currencies.GBP,
    Currencies.NGN,
    Currencies.USD,
    Currencies.ZAR,
  ]

  return !fiatCurrencies.includes(currency)
}

export const getCustomerWalletActions = ({
  accountCapabilities,
  customerWallet,
  isViewOnlyRole,
  customerModel,
}: UseCustomerWalletActionsProps): WalletActionOptions => {
  const isEmbeddedOrRelianceCustomerModel =
    customerModel === CustomerModels.EMBEDDED || customerModel === CustomerModels.RELIANCE

  const {
    isEpcWalletPayoutsCapabilityEnabled,
    isEpcWalletPayinsCapabilityEnabled,
    isEpcWalletInternalTransfersCapabilityEnabled,
  } = accountCapabilities || {}

  const { supportsInternalTransfers, supportsDeposits, supportsThirdParty, status } =
    customerWallet || {}
  const isActive = status === CustomerWalletStatusType.ACTIVE

  // Crypto sends and receives
  const { isCryptoSendOptionEnabled, isCryptoReceiveOptionEnabled, isCryptoMoveOptionEnabled } =
    getCryptoSendAndReceiveOptions({
      customerWallet,
      isEmbeddedOrRelianceCustomerModel,
      isViewOnlyRole,
      isActive,
      isEpcWalletPayoutsCapabilityEnabled,
      isEpcWalletPayinsCapabilityEnabled,
      isEpcWalletInternalTransfersCapabilityEnabled,
      supportsInternalTransfers,
    })

  // Fiat payins, payouts and internal transfers
  const { isUnifiedPayoutOptionEnabled, isFiatMoveOptionEnabled, isFiatDepositOptionEnabled } =
    getFiatPayinAndPayoutOptions({
      isEmbeddedOrRelianceCustomerModel,
      isViewOnlyRole,
      supportsThirdParty,
      isActive,
      isEpcWalletPayoutsCapabilityEnabled,
      isEpcWalletPayinsCapabilityEnabled,
      supportsInternalTransfers,
      supportsDeposits,
      isEpcWalletInternalTransfersCapabilityEnabled,
    })

  // Transaction export
  const isTransactionExportOptionEnabled = !isViewOnlyRole && isActive

  return {
    // Crypto
    isCryptoSendOptionEnabled,
    isCryptoReceiveOptionEnabled,
    isCryptoMoveOptionEnabled,
    // Fiat
    isUnifiedPayoutOptionEnabled,
    isFiatMoveOptionEnabled,
    isFiatDepositOptionEnabled,
    isTransactionExportOptionEnabled,
  }
}
